import React from "react";
import { Box, IconButton, Typography, useTheme } from "@mui/material";
import AppsIcon from "@mui/icons-material/Apps";
import ListIcon from "@mui/icons-material/List";

interface ViewToggleProps {
  isGridView: boolean;

  onToggle: (isGridView: boolean) => void;
  leftToggleIcon?: React.ReactNode;
  rightToggleIcon?: React.ReactNode;
  leftToggleText?: string;
  rightToggleText?: string;
  size?: "small" | "medium" | "large";
  sx?: any;
}

export const ViewToggle: React.FC<ViewToggleProps> = ({
  isGridView,
  onToggle,
  leftToggleIcon,
  leftToggleText,
  rightToggleIcon,
  rightToggleText,
  size = "medium",
  sx = {},
}) => {
  const theme = useTheme();
  const getSizeStyles = () => {
    const hasText = leftToggleText || rightToggleText;

    switch (size) {
      case "small":
        return {
          container: {
            width: hasText ? 160 : 60,
            height: hasText ? 40 : 32,
            minWidth: hasText ? 160 : 60,
          },
          iconButton: {
            width: hasText ? "auto" : 28,
            height: hasText ? "auto" : 28,
            minWidth: hasText ? 70 : 28,
            padding: hasText ? "4px 10px" : "4px",
          },
          icon: { fontSize: 16 },
          text: { fontSize: 12, marginLeft: hasText ? 4 : 0 },
        };
      case "large":
        return {
          container: {
            width: hasText ? 180 : 80,
            height: hasText ? 48 : 40,
            minWidth: hasText ? 180 : 80,
          },
          iconButton: {
            width: hasText ? "auto" : 36,
            height: hasText ? "auto" : 36,
            minWidth: hasText ? 80 : 36,
            padding: hasText ? "6px 14px" : "6px",
          },
          icon: { fontSize: 20 },
          text: { fontSize: 14, marginLeft: hasText ? 6 : 0 },
        };
      default: // medium
        return {
          container: {
            width: hasText ? 200 : 70,
            height: hasText ? 44 : 36,
            minWidth: hasText ? 200 : 70,
          },
          iconButton: {
            width: hasText ? "auto" : 32,
            height: hasText ? "auto" : 32,
            minWidth: hasText ? 90 : 32,
            padding: hasText ? "5px 12px" : "5px",
          },
          icon: { fontSize: 18 },
          text: { fontSize: 13, marginLeft: hasText ? 5 : 0 },
        };
    }
  };

  const sizeStyles = getSizeStyles();

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        backgroundColor: theme.palette.grey[200],
        borderRadius: "20px",
        padding: "2px",
        position: "relative",
        ...sizeStyles.container,
        ...sx,
      }}
    >
      {/* Active indicator background */}
      <Box
        sx={{
          position: "absolute",
          top: "2px",
          left: isGridView ? "2px" : "calc(50% + 1px)",
          width: "calc(50% - 2px)",
          height: "calc(100% - 4px)",
          backgroundColor: theme.palette.primary.main,
          borderRadius: "18px",
          transition: "left 0.3s ease-in-out",
          zIndex: 1,
        }}
      />

      {/* Grid View Button */}
      <IconButton
        onClick={() => onToggle(true)}
        sx={{
          position: "relative",
          zIndex: 2,
          color: isGridView
            ? theme.palette.primary.contrastText
            : theme.palette.text.secondary,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flex: 1,
          ...sizeStyles.iconButton,
          "&:hover": {
            backgroundColor: "transparent",
          },
        }}
      >
        {leftToggleIcon ? (
          leftToggleIcon
        ) : (
          <AppsIcon sx={{ ...sizeStyles.icon }} />
        )}

        {leftToggleText && (
          <Typography
            sx={{
              ...sizeStyles.text,
              whiteSpace: "nowrap",
              color: isGridView
                ? theme.palette.primary.contrastText
                : theme.palette.text.secondary,
              fontWeight: 500,
              display: "inline-block",
              lineHeight: 1,
            }}
          >
            {leftToggleText}
          </Typography>
        )}
      </IconButton>

      {/* List View Button */}
      <IconButton
        onClick={() => onToggle(false)}
        sx={{
          position: "relative",
          zIndex: 2,
          color: !isGridView
            ? theme.palette.primary.contrastText
            : theme.palette.text.secondary,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flex: 1,
          ...sizeStyles.iconButton,
          "&:hover": {
            backgroundColor: "transparent",
          },
        }}
      >
        {rightToggleIcon ? (
          rightToggleIcon
        ) : (
          <ListIcon sx={{ ...sizeStyles.icon }} />
        )}
        {rightToggleText && (
          <Typography
            sx={{
              ...sizeStyles.text,
              whiteSpace: "nowrap",
              color: !isGridView
                ? theme.palette.primary.contrastText
                : theme.palette.text.secondary,
              fontWeight: 500,
              display: "inline-block",
              lineHeight: 1,
            }}
          >
            {rightToggleText}
          </Typography>
        )}
      </IconButton>
    </Box>
  );
};
